import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatUSD } from "@/lib/api";
import { normalizeEstimate, safeNumber } from "@/lib/safe";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { FilePlus, FileText, MapPin, ArrowUpRight, Trash2, Plus } from "lucide-react";

const statusStyles = {
  draft: "border-slate-600 text-slate-300",
  analyzing: "border-amber-500 text-amber-400",
  ready: "border-emerald-500 text-emerald-400",
  error: "border-red-500 text-red-400",
};

export default function Dashboard() {
  const [projects, setProjects] = useState([]);
  const [estimates, setEstimates] = useState({});
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const { data } = await api.get("/projects");
      setProjects(data);
      // fetch any estimates that are ready
      const readyOnes = data.filter((p) => p.status === "ready");
      const results = await Promise.all(
        readyOnes.map((p) =>
          api
            .get(`/projects/${p.project_id}/estimate`)
            .then((r) => ({ id: p.project_id, est: normalizeEstimate(r.data) }))
            .catch(() => null)
        )
      );
      const map = {};
      results.forEach((r) => {
        if (r) map[r.id] = r.est;
      });
      setEstimates(map);
    } catch (e) {
      toast.error("Failed to load projects");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleDelete = async (id) => {
    try {
      await api.delete(`/projects/${id}`);
      toast.success("Project deleted");
      setProjects(projects.filter((p) => p.project_id !== id));
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const summary = {
    total: projects.length,
    ready: projects.filter((p) => p.status === "ready").length,
    analyzing: projects.filter((p) => p.status === "analyzing").length,
    estTotal: Object.values(estimates).reduce((s, e) => s + safeNumber(e?.total), 0),
  };

  return (
    <AppShell>
      <div data-testid="dashboard-page">
        {/* Header row */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#FF6A00] font-semibold mb-2">
              Workspace
            </div>
            <h1
              className="text-3xl md:text-4xl font-black tracking-tight text-white"
              style={{ fontFamily: "Chivo, sans-serif" }}
              data-testid="dashboard-heading"
            >
              Your estimates
            </h1>
            <p className="mt-2 text-slate-400">
              Every plan set you've analyzed, with running cost totals.
            </p>
          </div>
          <Button
            onClick={() => navigate("/projects/new")}
            className="bg-[#FF6A00] hover:bg-[#E65C00] text-white rounded-md"
            data-testid="dashboard-new-project-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            New estimate
          </Button>
        </div>

        {/* Stats grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[#2A2F38] border border-[#2A2F38] mb-10">
          {[
            { label: "Projects", value: summary.total },
            { label: "Ready", value: summary.ready },
            { label: "Analyzing", value: summary.analyzing },
            { label: "Total value", value: formatUSD(summary.estTotal) },
          ].map((s) => (
            <div key={s.label} className="bg-[#171A20] p-5">
              <div className="text-[10px] tracking-[0.25em] uppercase text-slate-500 font-semibold">
                {s.label}
              </div>
              <div
                className="mt-2 text-2xl md:text-3xl font-black text-white"
                style={{ fontFamily: "JetBrains Mono, monospace" }}
                data-testid={`stat-${s.label.toLowerCase().replace(/ /g, "-")}`}
              >
                {s.value}
              </div>
            </div>
          ))}
        </div>

        {/* Project list */}
        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-24 w-full bg-[#171A20]" />
            ))}
          </div>
        ) : projects.length === 0 ? (
          <div
            className="border border-dashed border-[#2A2F38] bg-[#171A20]/50 p-16 text-center"
            data-testid="empty-state"
          >
            <FilePlus className="h-10 w-10 text-slate-600 mx-auto" />
            <h3 className="mt-4 text-lg font-bold text-white" style={{ fontFamily: "Chivo, sans-serif" }}>
              No projects yet
            </h3>
            <p className="mt-2 text-sm text-slate-400">
              Upload your first plan set and let the AI build your estimate.
            </p>
            <Button
              onClick={() => navigate("/projects/new")}
              className="mt-6 bg-[#FF6A00] hover:bg-[#E65C00] text-white"
              data-testid="empty-state-cta"
            >
              <Plus className="h-4 w-4 mr-2" />
              Upload first plan
            </Button>
          </div>
        ) : (
          <div className="border border-[#2A2F38] bg-[#171A20] divide-y divide-[#2A2F38]" data-testid="projects-list">
            {projects.map((p) => {
              const est = estimates[p.project_id];
              return (
                <div
                  key={p.project_id}
                  className="grid grid-cols-12 gap-4 px-5 py-5 hover:bg-[#1F232B] transition-colors items-center"
                  data-testid={`project-row-${p.project_id}`}
                >
                  <div className="col-span-12 md:col-span-5">
                    <Link
                      to={`/projects/${p.project_id}`}
                      className="font-semibold text-white hover:text-[#FF6A00] transition-colors block"
                      data-testid={`project-name-${p.project_id}`}
                    >
                      {p.name}
                    </Link>
                    <div className="flex items-center gap-3 mt-1 text-xs text-slate-400">
                      {p.address && (
                        <span className="flex items-center gap-1">
                          <MapPin className="h-3 w-3" />
                          {p.address}
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <FileText className="h-3 w-3" />
                        {p.pdf_filename || "—"}
                      </span>
                    </div>
                  </div>
                  <div className="col-span-4 md:col-span-2">
                    <div className="text-[10px] tracking-[0.25em] uppercase text-slate-500">Type</div>
                    <div className="text-sm text-slate-200 mt-0.5">
                      {p.project_type.replace(/_/g, " ")}
                    </div>
                  </div>
                  <div className="col-span-4 md:col-span-2">
                    <div className="text-[10px] tracking-[0.25em] uppercase text-slate-500">Status</div>
                    <Badge
                      variant="outline"
                      className={`mt-1 ${statusStyles[p.status] || ""}`}
                      data-testid={`project-status-${p.project_id}`}
                    >
                      {p.status}
                    </Badge>
                  </div>
                  <div className="col-span-4 md:col-span-2 text-right md:text-left">
                    <div className="text-[10px] tracking-[0.25em] uppercase text-slate-500">Total</div>
                    <div
                      className="text-base font-bold text-white"
                      style={{ fontFamily: "JetBrains Mono, monospace" }}
                      data-testid={`project-total-${p.project_id}`}
                    >
                      {est ? formatUSD(est.total) : "—"}
                    </div>
                  </div>
                  <div className="col-span-12 md:col-span-1 flex items-center justify-end gap-1">
                    <Link
                      to={`/projects/${p.project_id}`}
                      className="p-2 text-slate-400 hover:text-white hover:bg-[#0F1115] rounded transition-colors"
                      data-testid={`open-project-${p.project_id}`}
                      title="Open"
                    >
                      <ArrowUpRight className="h-4 w-4" />
                    </Link>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <button
                          className="p-2 text-slate-400 hover:text-red-400 hover:bg-[#0F1115] rounded transition-colors"
                          data-testid={`delete-project-${p.project_id}`}
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </AlertDialogTrigger>
                      <AlertDialogContent className="bg-[#171A20] border-[#2A2F38] text-slate-200">
                        <AlertDialogHeader>
                          <AlertDialogTitle className="text-white">Delete project?</AlertDialogTitle>
                          <AlertDialogDescription className="text-slate-400">
                            "{p.name}" and its estimate will be permanently removed.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel className="bg-transparent border-[#2A2F38] text-slate-300 hover:bg-[#0F1115]">
                            Cancel
                          </AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => handleDelete(p.project_id)}
                            className="bg-red-600 hover:bg-red-700 text-white"
                            data-testid={`confirm-delete-${p.project_id}`}
                          >
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </AppShell>
  );
}
